import { useTranslation } from 'react-i18next';
import {
  InputProjectSimulator,
  SelectProjectSimulator,
} from '../../ProjectSimulator';
import { ListValueProps, projectSimulatorFieldRules } from '../../utils';
import GlazingSVG from '../../../../../assets/images/projectSimulator/GlazingSVG';
import { UseFormReturn } from 'react-hook-form';

type FacadeProps = {
  facade: FacadeData;
  form: UseFormReturn;
};

export type FacadeData = {
  glazing: {
    list: ListValueProps[];
    value: number;
  };
  occultation: {
    list: ListValueProps[];
    value: number;
  };

  technics: {
    list: ListValueProps[];
    value: number;
  };

  glazingFacadeRatio: number;
  roofSkylightArea: number;
  sunShaderArea: number;
};

function Facade({ facade, form }: FacadeProps) {
  const { t } = useTranslation();

  const facadeTechnicsList: ListValueProps[] = Object.values(facade.technics.list);
  const occultationsList: ListValueProps[] = Object.values(facade.occultation.list);
  const glazingList: ListValueProps[] = Object.values(facade.glazing.list);

  const facadeTechnicsValue: number = form.watch('facadeTechnics') ?? facade.technics.value;
  const facadeTechnicsIsSTEELWALL = facadeTechnicsValue === facade.technics.list.STEEL_WALL.id;
  return (
    <div className="form-block">
      <h2 className="form-title">
        {t('APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FACADE.TITLE', {
          defaultValue: 'Façade',
        })}
      </h2>

      <hr className="line-title-separation" />

      <div style={{ paddingLeft: '24px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '20px',
          }}
        >
          <label className="form-label">
            {t(
              'APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.LABEL.FACADE.TECHNICS',
              {
                defaultValue: 'Technique de façades',
              }
            )}
          </label>
          <SelectProjectSimulator
            form={form}
            name="facadeTechnics"
            listValues={facadeTechnicsList}
            defaultValue={facade.technics.value}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '20px',
          }}
        >
          <label className="form-label">
            {t('APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.LABEL.OCCULTATION', {
              defaultValue: 'Occultation',
            })}
          </label>
          <SelectProjectSimulator
            form={form}
            name="facadeOccultation"
            listValues={occultationsList}
            defaultValue={facade.occultation.value}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '20px',
          }}
        >
          <label className="form-label" style={{ marginBottom: 0 }}>
            {t(
              'APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.LABEL.GLAZING.FACADE.RATIO',
              {
                defaultValue: 'Ratio de vitrage / façade',
              }
            )}
          </label>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              columnGap: '32px',
            }}
          >
            <InputProjectSimulator
              form={form}
              name="glazingFacadeRatio"
              defaultValue={facade.glazingFacadeRatio / 100}
              style={{ maxWidth: '30%' }}
              rules={projectSimulatorFieldRules.ratioNumber}
            />
            <GlazingSVG />
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '20px',
          }}
        >
          <label className="form-label">
            {t('APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.LABEL.GLAZING', {
              defaultValue: 'Vitrage',
            })}
          </label>
          <SelectProjectSimulator
            disabled={facadeTechnicsIsSTEELWALL}
            form={form}
            name="facadeGlazing"
            listValues={facadeTechnicsIsSTEELWALL ? facade.glazing.list.DOUBLE : glazingList}
            defaultValue={facadeTechnicsIsSTEELWALL ? facade.glazing.list.DOUBLE.id : facade.glazing.value}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '32px',
          }}
        >
          <label className="form-label">
            {t(
              'APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.LABEL.SUN.SHADER.AREA',
              {
                defaultValue: 'Surface de brises soleil (m²)',
              }
            )}
          </label>
          <InputProjectSimulator
            form={form}
            defaultValue={facade.sunShaderArea}
            name="sunShaderArea"
            style={{ width: '30%' }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <label className="form-label">
            {t(
              'APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.LABEL.ROOF.SKYLIGHT',
              {
                defaultValue: 'Lanterneau en toiture',
              }
            )}
          </label>
          <label className="form-label">
            {t(
              'APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.LABEL.ROOF.SKYLIGHT.AREA',
              {
                defaultValue: 'Surface (m²)',
              }
            )}
          </label>
          <InputProjectSimulator
            form={form}
            defaultValue={facade.roofSkylightArea}
            name="roofSkylightArea"
            style={{ width: '30%' }}
          />
        </div>
      </div>
    </div>
  );
}

export default Facade;
